import { ShieldCheck, Landmark, GitMerge, BookOpenCheck, Compass } from "lucide-react";

export const FIRM = {
  name: "VCAJ & Associates LLP",
  tagline: "Chartered Accountants",
  phone: "+91 90809 33171",
  phoneHref: "tel:+919080933171",
  email: "info@vcaj.in",
  address: ["Willingdon Crescent, 4th Floor, #6/2,", "Dr. S. S. Badrinath Road, Nungambakkam,", "Chennai 600 006"],
  founded: 2017,
  linkedin: "https://www.linkedin.com/company/v-c-a-j-associates-llp/",
};

export const HERO = {
  eyebrow: "",
  lines: ["Adding value.", "Building trust.", "Leading excellence."],
  intro: "A new-age chartered accountancy firm bringing three decades of Big Four experience to ambitious businesses. Research-led, technology-enabled, partner-driven engagements.",
};
export const SEO_OVERRIDES = {};

const fill = (target, src) => Object.entries(src || {}).forEach(([k, v]) => { if (v !== "" && v != null && !(Array.isArray(v) && !v.filter(Boolean).length)) target[k] = Array.isArray(v) ? v.filter(Boolean) : v; });

// Apply portal-edited content over the built-in defaults.
export function applySiteSettings(s = {}) {
  const h = { ...(s.hero || {}) };
  if (Array.isArray(h.lines)) h.lines = HERO.lines.map((d, i) => h.lines[i] || d);
  fill(HERO, h);
  const c = { ...(s.contact || {}) };
  if (typeof c.address === "string") c.address = c.address.split("\n").map((l) => l.trim()).filter(Boolean);
  if (c.phone) c.phoneHref = `tel:${c.phone.replace(/[^\d+]/g, "")}`;
  fill(FIRM, c);
  Object.entries(s.services || {}).forEach(([slug, o]) => {
    const svc = SERVICES.find((x) => x.slug === slug);
    if (!svc || !o) return;
    const { scope, ...rest } = o;
    fill(svc, rest);
    if (Array.isArray(scope) && scope.some((g) => g?.title)) svc.scope = scope.filter((g) => g?.title).map((g) => ({ title: g.title, items: (g.items || []).filter(Boolean) }));
  });
  Object.assign(SEO_OVERRIDES, s.seo || {});
}

export const IMAGES = {
  home: "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?auto=format&fit=crop&w=1920&q=70",
  team: "https://images.unsplash.com/photo-1770992176317-382f51aff35d?auto=format&fit=crop&w=1920&q=70",
  careers: "https://images.unsplash.com/photo-1780639681636-d5e1878d567e?auto=format&fit=crop&w=1920&q=70",
  learn: "https://images.unsplash.com/photo-1573219093925-cea25da23f54?auto=format&fit=crop&w=1920&q=70",
  tools: "https://images.unsplash.com/photo-1709880945165-d2208c6ad2ec?auto=format&fit=crop&w=1920&q=70",
  contact: "https://images.unsplash.com/photo-1770992161135-e566047ddc22?auto=format&fit=crop&w=1920&q=70",
  "audit-risk": "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1920&q=70",
  "tax-regulatory": "https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=1920&q=70",
  transactions: "https://images.unsplash.com/photo-1745970649957-b4b1f7fde4ea?auto=format&fit=crop&w=1920&q=70",
  "accounting-reporting": "https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d?auto=format&fit=crop&w=1920&q=70",
  "strategic-finance": "https://images.unsplash.com/photo-1783446114502-2fb76307066b?auto=format&fit=crop&w=1920&q=70",
};


export const STANDARDS = [
  ["Standards on Auditing", "https://www.icai.org"],
  ["Ind AS", "https://www.icai.org/category/accounting-standards-board"],
  ["Companies Act, 2013", "https://www.mca.gov.in/content/mca/global/en/acts-rules/companies-act/companies-act-2013.html"],
  ["Income-tax Act", "https://www.incometaxindia.gov.in"],
  ["CGST Act", "https://cbic-gst.gov.in/gst-acts.html"],
  ["FEMA, 1999", "https://www.rbi.org.in/Scripts/Fema.aspx"],
  ["SEBI LODR", "https://www.sebi.gov.in/sebiweb/home/HomeAction.do?doListing=yes&sid=1&ssid=3&smid=0"],
  ["ICAI Code of Ethics", "https://www.icai.org/post/ethical-standards-board"],
  ["CARO 2020", "https://www.mca.gov.in/content/mca/global/en/acts-rules/companies-act/companies-act-2013.html"],
  ["RBI Directions", "https://www.rbi.org.in/Scripts/BS_ViewMasterDirections.aspx"],
];

export const SERVICES = [
  {
    slug: "audit-risk",
    no: "01",
    icon: ShieldCheck,
    title: "Audit & Risk Assurance",
    short: "Statutory, internal and IT audits that go beyond the opinion — into the controls and risks that matter.",
    hero: "Assurance your board, lenders and regulators can rely on — delivered with a partner on every file.",
    seoTitle: "Audit & Risk Assurance Services",
    seoDesc: "Statutory audit, internal audit, IFC/ICFR testing, IT and forensic audits by VCAJ & Associates LLP, Chartered Accountants.",
    scope: [
      { title: "Statutory & Tax Audit", items: ["Statutory audits under the Companies Act, 2013", "Tax audit under the Income-tax Act", "Limited reviews for listed entities", "Bank, branch and concurrent audits"] },
      { title: "Internal Audit & Controls", items: ["Risk-based internal audit programmes", "IFC / ICFR design and operating-effectiveness testing", "Standard operating procedures and risk control matrices", "Process audits — procure-to-pay, order-to-cash, record-to-report"] },
      { title: "Specialised Assurance", items: ["IT general controls and system audits", "Forensic reviews and fraud investigations", "Stock and fixed asset verification", "Certifications for regulators, lenders and grant bodies"] },
    ],
    approach: [
      { t: "Understand", d: "Walk-throughs with management to map the business, systems and key risks." },
      { t: "Plan", d: "Materiality, risk assessment and a sampling plan agreed with the partner." },
      { t: "Execute", d: "Fieldwork on our audit platform with documented working papers for every assertion." },
      { t: "Report", d: "Clear opinion, management letter and a prioritised action list — not just findings." },
    ],
    clients: ["Listed and unlisted companies", "Banks and NBFCs", "Private-equity backed businesses", "Not-for-profits and trusts"],
    frameworks: ["Standards on Auditing (SA)", "CARO 2020", "Ind AS / AS", "Section 143(3)(i) — IFC", "SEBI LODR", "RBI audit directions"],
    faqs: [
      { q: "When should a company appoint its statutory auditor?", a: "The first auditor is appointed by the Board within 30 days of incorporation; thereafter the auditor is appointed at the AGM for a term of five years, subject to rotation rules." },
      { q: "Do you provide internal audit and statutory audit to the same client?", a: "Independence rules restrict certain combinations. We assess each mandate against the Companies Act and ICAI Code of Ethics before accepting." },
      { q: "How long does a typical statutory audit take?", a: "For a mid-sized company, 3–6 weeks of fieldwork depending on readiness of books, group structure and number of locations." },
    ],
  },
  {
    slug: "tax-regulatory",
    no: "02",
    icon: Landmark,
    title: "Tax & Regulatory",
    short: "Direct tax, GST, transfer pricing and FEMA — compliance done right, positions defended well.",
    hero: "Tax positions that stand up to scrutiny, and compliance that never keeps you up at night.",
    seoTitle: "Direct & Indirect Tax Advisory Services",
    seoDesc: "Income tax, GST, transfer pricing, FEMA/RBI and representation services by VCAJ & Associates LLP, Chartered Accountants in India.",
    scope: [
      { title: "Direct Tax", items: ["Corporate and individual tax compliance", "Advance tax, TDS/TCS reviews", "Tax structuring and opinions", "Assessment, appeal and representation before authorities"] },
      { title: "GST & Indirect Tax", items: ["GST registration, returns and annual reconciliations", "ITC optimisation and health checks", "Notices, audits and departmental proceedings", "Customs and foreign trade policy advisory"] },
      { title: "International & Regulatory", items: ["Transfer pricing studies and benchmarking", "FEMA — FDI, ODI and ECB compliance", "RBI filings and compounding", "Company law — ROC, LLP and secretarial support"] },
    ],
    approach: [
      { t: "Diagnose", d: "Review of past filings, open notices and exposure areas." },
      { t: "Position", d: "Documented tax positions backed by law, circulars and case precedent." },
      { t: "Comply", d: "Calendar-driven filings with maker-checker review." },
      { t: "Defend", d: "Partner-led representation through assessment and appeal." },
    ],
    clients: ["Domestic corporates", "Indian subsidiaries of multinationals", "Start-ups and founders", "High-net-worth families"],
    frameworks: ["Income-tax Act", "CGST / IGST Acts", "FEMA, 1999", "Companies Act, 2013", "LLP Act, 2008", "OECD TP Guidelines"],
    faqs: [
      { q: "Can you handle notices from both income tax and GST departments?", a: "Yes. Our tax team prepares responses, attends hearings and represents clients up to the appellate tribunal level." },
      { q: "Is transfer pricing documentation needed for every group company?", a: "It is mandatory where international or specified domestic transactions cross the prescribed thresholds; we confirm applicability before you incur cost." },
      { q: "Do you support FDI reporting to the RBI?", a: "Yes — FC-GPR, FC-TRS, annual FLA returns and compounding applications where delays have occurred." },
    ],
  },
  {
    slug: "transactions",
    no: "03",
    icon: GitMerge,
    title: "Transactions & M&A",
    short: "Due diligence, valuation and deal structuring — so you know exactly what you are buying, selling or raising.",
    hero: "Clarity before you sign: diligence, valuation and structuring under one team.",
    seoTitle: "Transaction Advisory & M&A Services",
    seoDesc: "Financial and tax due diligence, business valuation, deal structuring and post-deal integration by VCAJ & Associates LLP.",
    scope: [
      { title: "Due Diligence", items: ["Financial due diligence — quality of earnings, net debt, working capital", "Tax due diligence — direct, indirect and TP exposures", "Vendor due diligence for sell-side processes", "Compliance and regulatory diligence"] },
      { title: "Valuation", items: ["Business and equity valuation", "Registered-valuer reports for Companies Act and FEMA", "ESOP and instrument valuation", "Purchase price allocation under Ind AS 103"] },
      { title: "Structuring & Integration", items: ["Mergers, demergers and slump sales", "Fund-raise readiness and data rooms", "Shareholder agreement financial terms", "Post-deal integration and 100-day plans"] },
    ],
    approach: [
      { t: "Scope", d: "Agree red-flag areas with the deal team and counsel." },
      { t: "Analyse", d: "Normalise earnings, test assumptions and quantify exposures." },
      { t: "Advise", d: "Translate findings into price, warranty and structure recommendations." },
      { t: "Close", d: "Support through SPA negotiation, completion accounts and integration." },
    ],
    clients: ["Private-equity and venture funds", "Strategic acquirers", "Promoters exploring a sale", "Start-ups raising capital"],
    frameworks: ["Ind AS 103 / 113", "Companies Act — Sec 230-232", "FEMA pricing guidelines", "Income-tax Rule 11UA", "ICAI Valuation Standards"],
    faqs: [
      { q: "How long does a financial due diligence take?", a: "Typically 3–5 weeks from data-room access, depending on the size of the target and responsiveness of management." },
      { q: "Can you issue valuation reports as a registered valuer?", a: "Yes, where required under the Companies Act, FEMA or income-tax rules, through our registered-valuer network." },
      { q: "Do you work on sell-side mandates?", a: "Yes — vendor due diligence and information-memorandum support help promoters run a cleaner, faster process." },
    ],
  },
  {
    slug: "accounting-reporting",
    no: "04",
    icon: BookOpenCheck,
    title: "Accounting & Reporting",
    short: "Books, closes and financial statements that are accurate, on time and audit-ready.",
    hero: "A finance function that closes on time, every month — whether we run it or strengthen yours.",
    seoTitle: "Accounting & Financial Reporting Services",
    seoDesc: "Bookkeeping, monthly close, Ind AS conversion, consolidation and payroll services by VCAJ & Associates LLP, Chartered Accountants.",
    scope: [
      { title: "Accounting Operations", items: ["Bookkeeping on Tally, Zoho and ERP systems", "Monthly and quarterly closes", "Accounts payable and receivable management", "Payroll processing and statutory compliance"] },
      { title: "Financial Reporting", items: ["Preparation of financial statements under Schedule III", "Ind AS conversion and technical accounting memos", "Group consolidation", "Lender and investor reporting packs"] },
      { title: "Controls & Systems", items: ["Chart-of-accounts redesign", "Accounting policy manuals", "Reconciliation frameworks", "ERP implementation support"] },
    ],
    approach: [
      { t: "Assess", d: "Current-state review of books, systems and close calendar." },
      { t: "Set up", d: "Policies, chart of accounts and a documented close checklist." },
      { t: "Run", d: "Monthly execution with reviewer sign-off and variance commentary." },
      { t: "Report", d: "MIS and statutory statements your auditors can rely on." },
    ],
    clients: ["SMEs and family businesses", "Indian arms of foreign companies", "Funded start-ups", "Trusts and societies"],
    frameworks: ["Ind AS", "Accounting Standards (AS)", "Schedule III", "Payroll laws — PF, ESI, PT", "GST & TDS"],
    faqs: [
      { q: "Can you work on our existing accounting software?", a: "Yes. We work on Tally Prime, Zoho Books, SAP Business One and most cloud ERPs." },
      { q: "What is involved in an Ind AS transition?", a: "Impact assessment, opening balance sheet adjustments, policy choices under Ind AS 101 and comparative restatement — usually over one to two quarters." },
      { q: "Do you also handle payroll?", a: "Yes — salary processing, payslips, PF/ESI/PT filings and Form 16 issuance." },
    ],
  },
  {
    slug: "strategic-finance",
    no: "05",
    icon: Compass,
    title: "Strategic Finance & Virtual CFO",
    short: "Senior finance leadership on demand — budgets, cash, fund-raise and board reporting.",
    hero: "CFO-level thinking for growing businesses, without the full-time cost.",
    seoTitle: "Strategic Finance & Virtual CFO Services",
    seoDesc: "Virtual CFO, budgeting, cash-flow management, fund-raise support and board MIS from VCAJ & Associates LLP, Chartered Accountants.",
    scope: [
      { title: "Virtual CFO", items: ["Monthly board and investor MIS", "Budgeting, forecasting and variance analysis", "Cash-flow and working-capital management", "Banking relationships and credit facilities"] },
      { title: "Growth & Capital", items: ["Financial models and business plans", "Fund-raise readiness and investor Q&A", "Unit economics and pricing analysis", "Cap-table and ESOP planning"] },
      { title: "Performance", items: ["KPI dashboards", "Cost reduction reviews", "Profitability by product, customer and branch", "Finance team hiring and coaching"] },
    ],
    approach: [
      { t: "Baseline", d: "Understand the numbers, the team and the founders' goals." },
      { t: "Design", d: "Build the budget, KPI set and reporting rhythm." },
      { t: "Embed", d: "Run monthly reviews with management and the board." },
      { t: "Scale", d: "Support fund-raise, audits and the hire of a full-time CFO when ready." },
    ],
    clients: ["Founder-led start-ups", "Family-owned businesses", "Mid-market companies preparing to raise", "Subsidiaries needing local finance leadership"],
    frameworks: ["Board reporting best practice", "Investor reporting covenants", "Companies Act governance", "Ind AS / AS"],
    faqs: [
      { q: "How much time does a virtual CFO spend with us?", a: "Engagements are scoped by need — commonly 2 to 6 days a month, plus availability for board meetings and urgent decisions." },
      { q: "Will the virtual CFO work with our existing accountant?", a: "Yes. We supervise and upskill your team rather than replace it, unless you ask us to run accounting as well." },
      { q: "Can you help us prepare for a fund-raise?", a: "Yes — financial model, data room, diligence readiness and support through investor discussions." },
    ],
  },
];

export const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Careers / Articleship", "General enquiry"];

export const WHY = [
  { t: "Partner on every file", d: "A partner reviews the work and is reachable — not only at sign-off." },
  { t: "Big Four depth", d: "Three decades of combined Big Four experience, backed by in-house tools for Ind AS, valuation and tax." },
  { t: "Integrity first", d: "We follow the ICAI Code of Ethics and independence rules on every engagement." },
  { t: "One team, many disciplines", d: "Audit, tax, transactions and finance specialists working off one plan." },
];

export const SECTORS = ["Banking & NBFC", "Technology & SaaS", "Manufacturing", "Healthcare & Pharma", "Real Estate & Infra", "Retail & Consumer", "Education & Non-profit", "Logistics"];

export const PUB_CATEGORIES = ["Direct Tax", "GST & Indirect Tax", "Audit Standards", "Corporate Law", "ESG & Risk"];

export const NAV = [
  { label: "Team", to: "/team", id: "team" },
  { label: "Careers", to: "/careers", id: "careers" },
  { label: "Let's Learn", to: "/lets-learn", id: "lets-learn" },
  { label: "Tools", to: "/tools", id: "tools" },
  { label: "Life at VCAJ", to: "/life-at-vcaj", id: "gallery" },
  { label: "Contact", to: "/#contact", id: "contact" },
];

export const PUBLIC_TOOLS = [
  { id: "ind_as_116_lease", name: "Lease Accounting — Ind AS 116", tag: "Ind AS 116", desc: "ROU asset, lease liability, amortisation schedule, journal entries and disclosure note." },
  { id: "eir_calculator", name: "Effective Interest Rate (EIR)", tag: "Ind AS 109", desc: "EIR on borrowings with processing fees, with the amortised-cost working." },
  { id: "company_law_applicability", name: "Companies Act, 2013 — Applicability", tag: "Companies Act", desc: "Internal audit, secretarial audit, independent directors, committees, CSR and vigil mechanism." },
  { id: "caro_applicability", name: "CARO 2020 — Applicability", tag: "CARO 2020", desc: "Check whether the Companies (Auditor's Report) Order applies to the company." },
  { id: "csr_applicability", name: "CSR — Section 135 Applicability", tag: "Section 135", desc: "Net-worth, turnover and profit tests, with the 2% CSR obligation." },
];

export const initials = (name = "") =>
  name.replace(/\(.*?\)/g, "").replace(/^CA\s+/i, "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "VC";

export const isVideo = (path = "") => /\.(mp4|webm|mov)$/i.test(path);
export const linkedinEmbed = (url = "") => {
  const m = url.match(/urn:li:(activity|share|ugcPost):(\d+)/) || url.match(/(activity|share|ugcPost)[-:](\d{15,})/);
  return m ? `https://www.linkedin.com/embed/feed/update/urn:li:${m[1]}:${m[2]}` : "";
};
export const mediaUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/public/media/${path}`;
